/**
 * The team, their till PINs, and the clock.
 *
 * Two things the types enforce rather than leave to a comment.
 *
 * A PIN never travels back from the server: there is no `pin` field on
 * `Employee`, only `has_pin`. It cannot be rendered because it does not exist
 * to be rendered.
 *
 * `hourly_rate` and `labour_cost` are `string | null`, and null means "you may
 * not see this" — not zero. A UI that coalesces them to 0 tells a manager
 * without pay permission that their colleagues work for nothing.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ApiError } from './client';
import { useApi } from './provider';

export interface Employee {
  id: string;
  user_id: string | null;
  code: string;
  name: string;
  job_title: string | null;
  started_on: string | null;
  ended_on: string | null;
  is_active: boolean;
  /** Whether a PIN is set. The PIN itself is never returned by the API. */
  has_pin: boolean;
  /** Null means withheld, not zero. Only present for callers who may see pay. */
  hourly_rate: string | null;
  /**
   * Only kept where a rule pack treats young workers differently, and only
   * shown to people who can manage the team. Null otherwise.
   */
  date_of_birth?: string | null;
}

export interface StaffBreak {
  id: string;
  started_at: string;
  ended_at: string | null;
  is_paid: boolean;
}

export interface TimeEntry {
  id: string;
  employee_id: string;
  employee_name: string;
  location_id: string;
  business_date: string;
  started_at: string;
  ended_at: string | null;
  breaks: StaffBreak[];
  adjustment_reason: string | null;
  /** Null while the shift is still running. Nothing guesses at unfinished time. */
  paid_minutes: number | null;
  paid_hours: string | null;
  /** What the site's rule pack warned about as this was saved. Empty without one. */
  warnings?: string[];
}

export interface TimesheetLine {
  employee_id: string;
  employee_name: string;
  entries: number;
  paid_minutes: number;
  paid_hours: string;
  /** Shifts with no clock-out. Shown, not silently dropped from the total. */
  open_entries: number;
  labour_cost: string | null;
}

export interface AddEmployeeInput {
  code: string;
  name: string;
  user_id?: string;
  job_title?: string;
  hourly_rate?: string;
  started_on?: string;
}

export interface UpdateEmployeeInput {
  name?: string;
  job_title?: string;
  hourly_rate?: string;
  is_active?: boolean;
  ended_on?: string;
}

export function useEmployees(includeInactive = false) {
  const api = useApi();
  return useQuery({
    queryKey: ['staff', 'employees', includeInactive],
    queryFn: ({ signal }) =>
      api.get<Employee[]>(`/v1/staff/employees?include_inactive=${includeInactive}`, signal),
    staleTime: 60_000,
  });
}

export function useAddEmployee() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<Employee, ApiError, AddEmployeeInput>({
    mutationFn: (input) => api.post<Employee>('/v1/staff/employees', input),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['staff'] });
    },
  });
}

/** The roles somebody added for the till can be given. Owner is given on the Team page. */
export type TillRole = 'waiter' | 'cashier' | 'kitchen' | 'manager';

/**
 * Add somebody who works the till: a name, a role and a PIN, with no email
 * and no password. They sign in by PIN on a till somebody already opened.
 * Counts against the plan's logins. 409 with `field: 'pin'` for a PIN already
 * in use or too easy to guess; nobody is half-added.
 */
export function useAddTillPerson() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<Employee, ApiError, { name: string; role: TillRole; pin: string }>({
    mutationFn: (input) => api.post<Employee>('/v1/staff/till-people', input),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['staff'] });
      void queryClient.invalidateQueries({ queryKey: ['users'] });
      void queryClient.invalidateQueries({ queryKey: ['setup'] });
    },
  });
}

export function useUpdateEmployee() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<Employee, ApiError, { id: string; changes: UpdateEmployeeInput }>({
    mutationFn: ({ id, changes }) =>
      api.request<Employee>(`/v1/staff/employees/${id}`, { method: 'PATCH', body: changes }),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['staff'] });
    },
  });
}

/**
 * Set a till PIN.
 *
 * Returns nothing, and there is nothing to read back afterwards. A forgotten
 * PIN is replaced, not recovered — which is the point of hashing it.
 */
export function useSetPin() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<void, ApiError, { id: string; pin: string }>({
    mutationFn: ({ id, pin }) =>
      api.request<void>(`/v1/staff/employees/${id}/pin`, { method: 'PUT', body: { pin } }),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['staff'] });
    },
  });
}

export function useClearPin() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<void, ApiError, { id: string }>({
    mutationFn: ({ id }) =>
      api.request<void>(`/v1/staff/employees/${id}/pin`, { method: 'DELETE' }),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['staff'] });
    },
  });
}

/**
 * Who is clocked in right now.
 *
 * Polled rather than cached long: a manager looking at this is deciding whether
 * to send somebody home, and a stale answer is worse than a slow one.
 */
export interface Shift {
  id: string;
  location_id: string;
  employee_id: string;
  employee_name: string;
  business_date: string;
  starts_at: string;
  ends_at: string;
  role: string | null;
  note: string | null;
  /** Null while the rota is a draft: a draft is somebody thinking out loud. */
  published_at: string | null;
  scheduled_hours: string;
  /** What the site's rule pack says about this shift. Empty without one. */
  warnings?: string[];
}

export interface RotaLine {
  employee_id: string;
  employee_name: string;
  role: string | null;
  shift_id: string | null;
  starts_at: string | null;
  ends_at: string | null;
  scheduled_hours: string | null;
  /** From the punches. Null where they have not clocked in at all. */
  worked_from: string | null;
  worked_to: string | null;
  worked_hours: string | null;
  on_now: boolean;
  /** Minutes past the scheduled end. The only reason a bar is ever amber. */
  overrun_minutes: number;
  /** Clocked on with nothing scheduled — covering a no-show, usually. */
  unscheduled: boolean;
  /** What the site's rule pack says about the scheduled shift. */
  warnings?: string[];
}

export interface Rota {
  location_id: string;
  business_date: string;
  published: boolean;
  scheduled_hours: string;
  worked_hours: string;
  labour_cost: string;
  net_sales: string;
  /** Null where nothing sold: unanswerable rather than zero. */
  labour_percent: string | null;
  /** Null until somebody sets a ceiling. */
  target_percent: string | null;
  target_cost: string | null;
  over_by: string | null;
  on_now: number;
  past_end: number;
  covers: number;
  covers_per_labour_hour: string | null;
  lines: RotaLine[];
}

/**
 * One night: the plan and the punches side by side.
 *
 * Polled rather than cached hard, because the interesting half of it moves —
 * somebody is on past their end, the labour percentage climbs as the service
 * runs, and a screen answering with a figure from twenty minutes ago is a
 * screen somebody makes a staffing decision on.
 */
export function useRota(locationId: string | undefined, businessDate: string) {
  const api = useApi();
  return useQuery({
    queryKey: ['staff', 'rota', locationId ?? null, businessDate],
    queryFn: ({ signal }) =>
      api.get<Rota>(
        `/v1/staff/rota?location_id=${locationId}&business_date=${businessDate}`,
        signal,
      ),
    enabled: Boolean(locationId),
    staleTime: 15_000,
    refetchInterval: 60_000,
  });
}

export function useShifts(locationId: string | undefined, fromDate: string, toDate: string) {
  const api = useApi();
  return useQuery({
    queryKey: ['staff', 'shifts', locationId ?? null, fromDate, toDate],
    queryFn: ({ signal }) =>
      api.get<Shift[]>(
        `/v1/staff/shifts?location_id=${locationId}&from_date=${fromDate}&to_date=${toDate}`,
        signal,
      ),
    enabled: Boolean(locationId),
  });
}

function invalidateRota(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: ['staff', 'rota'] });
  void queryClient.invalidateQueries({ queryKey: ['staff', 'shifts'] });
}

export function useScheduleShift() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<
    Shift,
    ApiError,
    {
      locationId: string;
      employeeId: string;
      businessDate: string;
      startsAt: string;
      endsAt: string;
      role?: string;
    }
  >({
    mutationFn: (input) =>
      api.post<Shift>('/v1/staff/shifts', {
        location_id: input.locationId,
        employee_id: input.employeeId,
        business_date: input.businessDate,
        starts_at: input.startsAt,
        ends_at: input.endsAt,
        ...(input.role ? { role: input.role } : {}),
      }),
    onSettled: () => invalidateRota(queryClient),
  });
}

export function useRemoveShift() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<void, ApiError, { shiftId: string }>({
    mutationFn: ({ shiftId }) =>
      api.request<void>(`/v1/staff/shifts/${shiftId}`, { method: 'DELETE' }),
    onSettled: () => invalidateRota(queryClient),
  });
}

/** Turn a night's draft into a promise. */
export function usePublishRota() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<
    { shifts: number },
    ApiError,
    { locationId: string; businessDate: string }
  >({
    mutationFn: ({ locationId, businessDate }) =>
      api.post<{ shifts: number }>('/v1/staff/rota/publish', {
        location_id: locationId,
        business_date: businessDate,
      }),
    onSettled: () => invalidateRota(queryClient),
  });
}

/** Copy a night's rota onto another night, as a draft. */
export function useCopyRota() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<
    { shifts: number },
    ApiError,
    { locationId: string; fromDate: string; toDate: string }
  >({
    mutationFn: ({ locationId, fromDate, toDate }) =>
      api.post<{ shifts: number }>('/v1/staff/rota/copy', {
        location_id: locationId,
        from_date: fromDate,
        to_date: toDate,
      }),
    onSettled: () => invalidateRota(queryClient),
  });
}

export function useLabourTarget(locationId: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: ['staff', 'labour-target', locationId ?? null],
    queryFn: ({ signal }) =>
      api.get<{ location_id: string; percent: string | null }>(
        `/v1/staff/labour-target?location_id=${locationId}`,
        signal,
      ),
    enabled: Boolean(locationId),
    staleTime: 300_000,
  });
}

export function useSetLabourTarget() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<
    { location_id: string; percent: string | null },
    ApiError,
    { locationId: string; percent: string }
  >({
    mutationFn: ({ locationId, percent }) =>
      api.request<{ location_id: string; percent: string | null }>('/v1/staff/labour-target', {
        method: 'PUT',
        body: { location_id: locationId, percent },
      }),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['staff', 'labour-target'] });
      invalidateRota(queryClient);
    },
  });
}

export function useOnShift(locationId: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: ['staff', 'on-shift', locationId ?? null],
    queryFn: ({ signal }) =>
      api.get<TimeEntry[]>(`/v1/staff/on-shift?location_id=${locationId}`, signal),
    enabled: Boolean(locationId),
    staleTime: 15_000,
    refetchInterval: 30_000,
  });
}

/**
 * Where the signed-in person stands with the clock.
 *
 * `employee_id` is null when this login has no employee record — an owner who
 * set the restaurant up and never put themselves on the payroll. Ordinary
 * rather than broken, and the screen says so instead of offering a clock that
 * would refuse.
 */
export interface MyShift {
  employee_id: string | null;
  name: string | null;
  /** Null when off the clock. */
  started_at: string | null;
  /** Null when not on a break. Implies `started_at` is set. */
  break_started_at: string | null;
  on_shift: boolean;
  on_break: boolean;
}

/**
 * Am I on the clock, and as whom.
 *
 * The one question nothing else answers. `useOnDuty` lists colleagues already
 * on, which is no use to somebody who has just walked in, and finding yourself
 * in the employee list needs a manager's permission that the people who
 * actually clock in do not have.
 *
 * Polled, because a terminal is shared: somebody else may have clocked this
 * person out from the back office between one look and the next.
 */
export function useMyShift() {
  const api = useApi();
  return useQuery({
    queryKey: ['staff', 'me'],
    queryFn: ({ signal }) => api.get<MyShift>('/v1/staff/me', signal),
    staleTime: 15_000,
    refetchInterval: 60_000,
  });
}

export function useClockIn() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<TimeEntry, ApiError, { employeeId: string; locationId: string }>({
    mutationFn: ({ employeeId, locationId }) =>
      api.post<TimeEntry>('/v1/staff/clock-in', {
        employee_id: employeeId,
        location_id: locationId,
      }),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['staff'] });
    },
  });
}

export function useClockOut() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<TimeEntry, ApiError, { employeeId: string; locationId: string }>({
    mutationFn: ({ employeeId, locationId }) =>
      api.post<TimeEntry>('/v1/staff/clock-out', {
        employee_id: employeeId,
        location_id: locationId,
      }),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['staff'] });
    },
  });
}

export function useStartBreak() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<TimeEntry, ApiError, { employeeId: string; isPaid?: boolean }>({
    mutationFn: ({ employeeId, isPaid = false }) =>
      api.post<TimeEntry>('/v1/staff/breaks/start', {
        employee_id: employeeId,
        is_paid: isPaid,
      }),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['staff'] });
    },
  });
}

export function useEndBreak() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<TimeEntry, ApiError, { employeeId: string }>({
    mutationFn: ({ employeeId }) =>
      api.post<TimeEntry>('/v1/staff/breaks/end', { employee_id: employeeId }),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['staff'] });
    },
  });
}

export function useTimesheet(
  locationId: string | undefined,
  fromDate: string,
  toDate: string,
  roundToMinutes = 1,
) {
  const api = useApi();
  return useQuery({
    queryKey: ['staff', 'timesheet', locationId ?? null, fromDate, toDate, roundToMinutes],
    queryFn: ({ signal }) =>
      api.get<TimesheetLine[]>(
        `/v1/staff/timesheet?location_id=${locationId}&from_date=${fromDate}` +
          `&to_date=${toDate}&round_to_minutes=${roundToMinutes}`,
        signal,
      ),
    enabled: Boolean(locationId),
    staleTime: 30_000,
  });
}

/** "8h 15m" — how a rota is read, rather than 495. */
export function formatMinutes(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours === 0) return `${rest}m`;
  if (rest === 0) return `${hours}h`;
  return `${hours}h ${rest}m`;
}

/**
 * A period's tips and how they were shared.
 *
 * Tips were captured on every payment, totalled on the Z report, and allocated
 * to nobody. Since October 2024 the Employment (Allocation of Tips) Act
 * requires an employer in Great Britain to allocate them fairly, keep a
 * written policy, and produce a worker's own record on request — none of which
 * a running total can do.
 */
export interface TipPool {
  id: string;
  location_id: string;
  from_date: string;
  to_date: string;
  /** What the tills took, frozen when the pool was struck. */
  collected: string;
  method: 'hours' | 'points';
  note: string | null;
  shares: TipShare[];
}

export interface TipShare {
  employee_id: string;
  employee_name: string;
  hours: string;
  /**
   * The claim behind the money — hours, or hours times a role weighting.
   *
   * Worth showing next to the amount. "Why is mine smaller than hers" is a
   * question about this number, and it is one a worker is entitled to have
   * answered without anybody doing arithmetic in their head.
   */
  weight: string;
  amount: string;
  /** The period, on somebody's own record. Null inside a pool, which has one. */
  from_date?: string | null;
  to_date?: string | null;
}

/**
 * Past pools, most recent first.
 *
 * **These figures do not move.** A pool records a decision, so a punch
 * corrected three weeks later does not restate what somebody was already told
 * they were owed. If a screen shows a pool next to a live timesheet they will
 * disagree, and that is correct — say so rather than reconciling them.
 */
export function useTipPools(locationId: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: ['staff', 'tip-pools', locationId ?? null],
    queryFn: ({ signal }) =>
      api.get<TipPool[]>(`/v1/staff/tip-pools?location_id=${locationId}`, signal),
    enabled: Boolean(locationId),
    staleTime: 60_000,
  });
}

/**
 * One person's own tipping record.
 *
 * A worker may ask for this and the employer has to produce it. Its own call
 * so that answering does not require showing them what everybody else was
 * paid.
 */
export function useTipRecord(employeeId: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: ['staff', 'employees', employeeId ?? null, 'tips'],
    queryFn: ({ signal }) =>
      api.get<TipShare[]>(`/v1/staff/employees/${employeeId}/tips`, signal),
    enabled: Boolean(employeeId),
    staleTime: 60_000,
  });
}

/**
 * Work out who is owed what, and write it down. Professional and above.
 *
 * `hours` shares by time worked and needs no explaining to anybody. `points`
 * weights those hours per person — a section chef on 1.5 against a porter on
 * 1.0 — and anybody left out of `weights` counts as 1.
 *
 * A period nobody worked is refused with a 409, not banked. It means the hours
 * are missing or the dates are wrong, and the money should stay put until
 * somebody looks — so show that message rather than a generic failure.
 *
 * Striking a pool is not reversible from the API. Worth a confirmation step
 * showing the shares before it is written, because the figures freeze at that
 * moment and a mistake has to be corrected by a human rather than a button.
 */
export function useStrikeTipPool() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<
    TipPool,
    ApiError,
    {
      locationId: string;
      fromDate: string;
      toDate: string;
      method?: 'hours' | 'points';
      weights?: Record<string, string>;
      note?: string;
    }
  >({
    mutationFn: (input) =>
      api.post<TipPool>('/v1/staff/tip-pools', {
        location_id: input.locationId,
        from_date: input.fromDate,
        to_date: input.toDate,
        method: input.method ?? 'hours',
        weights: input.weights ?? {},
        note: input.note ?? null,
      }),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['staff', 'tip-pools'] });
    },
  });
}

/**
 * Tie an employee to the login they sign in with.
 *
 * The only way a member of staff can be given a role: roles live on logins,
 * and Team offers them to employees with a `user_id`. A login already tied to
 * somebody else is refused with a 409 naming `user_id` and carrying the other
 * `employee_id`, so the screen can say whose it is rather than just "taken".
 */
export function useLinkLogin() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<Employee, ApiError, { employeeId: string; userId: string }>({
    mutationFn: ({ employeeId, userId }) =>
      api.request<Employee>(`/v1/staff/employees/${employeeId}/login`, {
        method: 'PUT',
        body: { user_id: userId },
      }),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['staff'] });
    },
  });
}

/**
 * Separate an employee from their login. Neither is deleted: the person keeps
 * their timesheets and the login keeps its roles — it just stops being this
 * member of staff's.
 */
export function useUnlinkLogin() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<Employee, ApiError, { employeeId: string }>({
    mutationFn: ({ employeeId }) =>
      api.request<Employee>(`/v1/staff/employees/${employeeId}/login`, { method: 'DELETE' }),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['staff'] });
    },
  });
}

/** Somebody a tab can be handed to. */
export interface Colleague {
  user_id: string;
  name: string;
  on_shift: boolean;
}

/**
 * Who is around to take a table.
 *
 * Its own route rather than the user list or the on-shift list, both of which
 * need a manager's permission — `identity.user.read` and `staff.timesheet.read`
 * respectively, and the second carries hours. This carries a name and a login
 * id, which is what a handover needs and all it needs.
 */
export function useOnDuty(locationId: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: ['staff', 'on-duty', locationId],
    queryFn: ({ signal }) =>
      api.get<Colleague[]>(`/v1/staff/on-duty?location_id=${locationId}`, signal),
    enabled: Boolean(locationId),
    staleTime: 30_000,
  });
}

/** One person on one trading day, measured against the site's rules. */
export interface ComplianceLine {
  employee_id: string;
  employee_name: string;
  business_date: string;
  worked_minutes: number;
  break_owed_minutes: number;
  break_taken_minutes: number;
  break_short_minutes: number;
  overtime_minutes: number;
  /** A shift with no clock-out. Not yet late — it has not finished. */
  open_entries: number;
}

export interface ComplianceWeek {
  employee_id: string;
  employee_name: string;
  worked_minutes: number;
  overtime_minutes: number;
}

export interface Compliance {
  location_id: string;
  from_date: string;
  to_date: string;
  break_after_minutes: number | null;
  break_minutes: number | null;
  overtime_after_minutes: number | null;
  overtime_after_weekly_minutes: number | null;
  breaches: number;
  lines: ComplianceLine[];
  weeks: ComplianceWeek[];
  /** The rule pack the site is measured against, on Enterprise, if any. */
  pack_name?: string | null;
  /** Everything else the pack found: limits, rest, young workers. */
  findings?: ComplianceFinding[];
}

/** One thing a site's rule pack says about somebody's day. */
export interface ComplianceFinding {
  employee_id: string;
  employee_name: string;
  business_date: string;
  kind: string;
  applies_to: 'everyone' | 'minors';
  /** Whether the rota and clock warn about it ahead of time or refuse it. */
  mode: 'warn' | 'block';
  message: string;
}

export interface ComplianceRules {
  location_id: string;
  break_after_minutes: number | null;
  break_minutes: number | null;
  overtime_after_minutes: number | null;
  overtime_after_weekly_minutes: number | null;
}

/**
 * Who was owed a break and did not get one.
 *
 * Computed on every read rather than stored, so a rule changed today does not
 * rewrite last month and a breach is not a row somebody can delete.
 */
export function useCompliance(
  locationId: string | undefined,
  fromDate: string,
  toDate: string,
  { enabled = true }: { enabled?: boolean } = {},
) {
  const api = useApi();
  return useQuery({
    queryKey: ['staff', 'compliance', locationId, fromDate, toDate],
    queryFn: ({ signal }) =>
      api.get<Compliance>(
        `/v1/staff/compliance?location_id=${locationId}&from_date=${fromDate}&to_date=${toDate}`,
        signal,
      ),
    enabled: enabled && Boolean(locationId),
    staleTime: 30_000,
  });
}

export function useComplianceRules(locationId: string | undefined, enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: ['staff', 'compliance-rules', locationId],
    queryFn: ({ signal }) =>
      api.get<ComplianceRules>(`/v1/staff/compliance-rules?location_id=${locationId}`, signal),
    enabled: enabled && Boolean(locationId),
    staleTime: 60_000,
  });
}

export function useSetComplianceRules() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<
    ComplianceRules,
    ApiError,
    {
      locationId: string;
      breakAfterMinutes: number | null;
      breakMinutes: number | null;
      overtimeAfterMinutes: number | null;
      overtimeAfterWeeklyMinutes: number | null;
    }
  >({
    mutationFn: (input) =>
      api.request<ComplianceRules>('/v1/staff/compliance-rules', {
        method: 'PUT',
        body: {
          location_id: input.locationId,
          break_after_minutes: input.breakAfterMinutes,
          break_minutes: input.breakMinutes,
          overtime_after_minutes: input.overtimeAfterMinutes,
          overtime_after_weekly_minutes: input.overtimeAfterWeeklyMinutes,
        },
      }),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['staff'] });
    },
  });
}

/**
 * Record or clear somebody's date of birth, for young-worker rules. Enterprise,
 * with the rule packs that read it; nothing else uses it.
 */
export function useSetDateOfBirth() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<Employee, ApiError, { employeeId: string; dateOfBirth: string | null }>({
    mutationFn: ({ employeeId, dateOfBirth }) =>
      api.request<Employee>(`/v1/staff/employees/${employeeId}/date-of-birth`, {
        method: 'PUT',
        body: { date_of_birth: dateOfBirth },
      }),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['staff'] });
    },
  });
}
